import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiClient, reportLabel, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import HouseholdMembersStrip from "@/components/HouseholdMembersStrip";
import SoloHouseholdIncomeCard from "@/components/SoloHouseholdIncomeCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Upload, Paperclip, Download, FileSignature, Mail, BarChart3, Archive, ArchiveRestore, Users, ArrowUpRight, Trash2, ListChecks, Briefcase, Home, ShieldCheck, Hash, Pencil, Check, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import DecisionPointReport from "@/components/DecisionPointReport";
import DecisionPointEntryForm from "@/components/DecisionPointEntryForm";
import WorkflowPanel from "@/components/WorkflowPanel";
import DangerZone from "@/components/DangerZone";
import VerificationPipelineStepper from "@/components/VerificationPipelineStepper";
import AdditionalInfoPanel from "@/components/AdditionalInfoPanel";
import RiskIndicatorsSection from "@/components/RiskIndicatorsSection";
import { wasProductOrdered, effectiveOrderedProducts } from "@/lib/orderedProducts";
import DecisionPointSummaryCard from "@/components/DecisionPointSummaryCard";
import ExecutiveSummaryStripe from "@/components/ExecutiveSummaryStripe";
import VerificationFindingsCard from "@/components/VerificationFindingsCard";
import { VerificationEntriesDisplay } from "@/components/VerificationEntries";
import PMEmploymentSummaryCard from "@/components/PMEmploymentSummaryCard";
import PMLandlordSummaryCard from "@/components/PMLandlordSummaryCard";
import PMDocumentValidationSummaryCard from "@/components/PMDocumentValidationSummaryCard";
import DecisionHero from "@/components/DecisionHero";
import ModifyOrderDialog from "@/components/ModifyOrderDialog";
import OrderHistoryCard from "@/components/OrderHistoryCard";
import ReportRow from "@/components/ReportRow";
import CaseCommunicationLog from "@/components/CaseCommunicationLog";
import { stageFor } from "@/lib/verifyHub";
import { fmtDate, fmtDateTime } from "@/lib/dateFormat";

/**
 * Phase G.2 — inline NTN Inquiry # header pill.
 * Staff: view + inline edit. PMs: read-only when assigned, hidden
 * when not yet assigned (per spec — never show "Not Assigned" to PMs).
 */
function InquiryNumberInline({ screeningId, value, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [busy, setBusy] = useState(false);
  // Hide the whole field for non-staff callers when nothing is assigned.
  if (!canEdit && !value) return null;

  const save = async () => {
    const val = (draft || "").trim();
    if (val && !/^\d{1,32}$/.test(val)) {
      toast.error("Inquiry # must be digits only (1–32 characters).");
      return;
    }
    setBusy(true);
    try {
      await apiClient.patch(`/staff-ops/case/${screeningId}/inquiry-number`, { value: val || null });
      toast.success(val ? "Inquiry # updated" : "Inquiry # cleared");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d[0]?.msg : "Could not save");
    } finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className="ntn-mono flex items-center gap-2" data-testid="inquiry-inline-editor">
        <Hash className="h-3.5 w-3.5" />
        <span>INQUIRY #</span>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/\D/g, ""))}
          maxLength={32}
          autoFocus
          inputMode="numeric"
          pattern="[0-9]*"
          className="border border-[#0A192F]/20 rounded-sm px-2 py-1 text-xs font-mono w-40 focus:outline-none focus:ring-2 focus:ring-[#0A192F]/20"
          data-testid="inquiry-inline-input"
          placeholder="e.g. 00123456"
        />
        <button onClick={save} disabled={busy}
                className="text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
                data-testid="inquiry-inline-save"><Check className="h-4 w-4" /></button>
        <button onClick={() => { setEditing(false); setDraft(value || ""); }}
                className="text-slate-500 hover:text-slate-800"
                data-testid="inquiry-inline-cancel"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <div className="ntn-mono flex items-center gap-2" data-testid="inquiry-inline">
      <Hash className="h-3.5 w-3.5" />
      <span>INQUIRY #</span>
      {value ? (
        <span className="text-[#0A192F]" data-testid="inquiry-inline-value">{value}</span>
      ) : (
        <span className="italic text-slate-400" data-testid="inquiry-inline-empty">Not Assigned</span>
      )}
      {canEdit && (
        <button onClick={() => { setDraft(value || ""); setEditing(true); }}
                className="text-slate-400 hover:text-slate-800 ml-1"
                data-testid="inquiry-inline-edit-btn"
                aria-label="Edit inquiry number">
          <Pencil className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export default function ScreeningDetailPage() {
  const { id } = useParams();
  const { user, hasPermission } = useAuth();
  const [s, setS] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");
  // Phase D: default tab depends on documents.view permission — users
  // without doc access should land on Reports rather than a hidden tab.
  const _canDocs = user?.role === "ntn_staff" || user?.role === "applicant" || hasPermission?.("documents.view");
  const [tab, setTab] = useState(_canDocs ? "documents" : "reports");
  const fileInputRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const [sc, ms, wn] = await Promise.all([
        apiClient.get(`/screenings/${id}`),
        apiClient.get(`/screenings/${id}/messages`),
        // Workflow notes — server enforces visibility (PM/applicant only
        // ever receive property_manager-visible notes).
        apiClient.get(`/screenings/${id}/workflow/notes`).catch(() => ({ data: [] })),
      ]);
      const noteMsgs = (wn.data || []).map((n) => ({
        id: `note-${n.id}`, sender_id: n.author_id, sender_name: n.author_name,
        sender_role: "ntn_staff", content: n.content, created_at: n.created_at,
        kind: "note", visibility: n.visibility,
      }));
      const merged = [...ms.data, ...noteMsgs].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at));
      setS(sc.data); setMessages(merged);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    // Tell the bell + dashboard indexes that we've viewed this screening
    window.dispatchEvent(new CustomEvent("ntn:notifications:refresh"));
  }, [id]);

  const updateReport = async (type, status, summary) => {
    try {
      const { data } = await apiClient.patch(`/screenings/${id}/reports/${type}`, { status, result_summary: summary || null });
      setS(data); toast.success("Report updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendMessage = async (content) => {
    try {
      const { data } = await apiClient.post(`/screenings/${id}/messages`, { content });
      setMessages((m) => [...m, data]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true);
    let okCount = 0;
    for (let i = 0; i < list.length; i++) {
      const file = list[i];
      const fd = new FormData();
      fd.append("file", file);
      try {
        await apiClient.post(`/files/upload?screening_id=${id}&label=document`, fd);
        okCount += 1;
      } catch (err) {
        toast.error(`${file.name}: ${formatApiError(err.response?.data?.detail) || "upload failed"}`);
      }
    }
    setUploading(false);
    if (okCount === list.length) {
      toast.success(okCount > 1 ? `${okCount} files uploaded` : "File uploaded");
    } else if (okCount > 0) {
      toast.success(`${okCount} of ${list.length} files uploaded`);
    }
    load();
  };

  const onDropFiles = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const dropped = e.dataTransfer?.files;
    if (dropped?.length) uploadFiles(dropped);
  };

  const uploadFile = async (e) => {
    await uploadFiles(e.target.files);
    if (e.target) e.target.value = "";
  };

  const deleteFile = async (fileId) => {
    try {
      await apiClient.delete(`/files/${fileId}`);
      toast.success("Document removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete");
    }
  };

  const claim = async () => {
    try {
      const { data } = await apiClient.patch(`/screenings/${id}/assign`);
      setS(data); toast.success("Assigned to you");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportPdf = async () => {
    try {
      const res = await apiClient.get(`/screenings/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NTN-${id.slice(0, 8).toUpperCase()}-${s.applicant_last_name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "PDF export failed");
    }
  };

  const toggleArchive = async () => {
    try {
      const path = s.archived_at ? "unarchive" : "archive";
      const { data } = await apiClient.post(`/screenings/${id}/${path}`);
      setS(data);
      toast.success(s.archived_at ? "Restored from archive" : "Archived");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update archive state");
    }
  };

  const exportApplicationPdf = async () => {
    try {
      const res = await apiClient.get(`/screenings/${id}/application/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NTN-CA-Application-${id.slice(0, 8).toUpperCase()}-${s.applicant_last_name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Application PDF downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "PDF export failed");
    }
  };

  const [sendOpen, setSendOpen] = useState(false);
  const [sendFee, setSendFee] = useState("");
  const [sendReceipt, setSendReceipt] = useState("");
  const [sendNote, setSendNote] = useState("");
  const [sendBusy, setSendBusy] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const sendApplicationLink = async () => {
    setSendBusy(true);
    try {
      const { data } = await apiClient.post(`/screenings/${id}/application/send`, {
        screening_fee_amount: sendFee ? parseFloat(sendFee) : null,
        screening_fee_receipt: sendReceipt || null,
        custom_message: sendNote || null,
      });
      setSendResult(data);
      toast.success("Application link sent to applicant");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to send"); }
    finally { setSendBusy(false); }
  };

  if (error) return <AppShell><div className="text-[#EF4444]">{error}</div></AppShell>;
  if (!s) return <AppShell><div className="font-overline text-[#4A5568]">Loading…</div></AppShell>;

  const isStaff = user?.role === "ntn_staff";
  const requestedReports = s.reports.filter((r) => r.requested);
  const completedCount = requestedReports.filter((r) => r.status === "complete").length;

  return (
    <AppShell>
      <Link to="/dashboard" className="inline-flex items-center text-sm text-[#4A5568] hover:text-[#0A192F] mb-6" data-testid="back-link">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to dashboard
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 ntn-stagger">
        {/* Header card — hoisted to the top so PMs see the applicant identity
             + primary actions before anything else. */}
        <div className="lg:col-span-12 bg-white border border-[#0A192F]/10 rounded-sm p-8">
          {s.archived_at && (
            <div className="mb-5 inline-flex items-center gap-2 text-xs font-mono tracking-[0.14em] uppercase bg-[#F3EFEA] border border-[#0A192F]/15 px-3 py-1.5 rounded-sm" data-testid="archived-banner">
              <Archive className="h-3.5 w-3.5" /> Archived {fmtDate(s.archived_at)}
              {s.archived_by_name && <span>· by {s.archived_by_name}</span>}
            </div>
          )}
          {s.group_id && (
            <Link to={`/groups/${s.group_id}`} className="mb-5 inline-flex items-center gap-2 text-xs font-mono tracking-[0.14em] uppercase bg-[#0A192F] text-white border border-[#0A192F] hover:bg-[#0A192F]/90 px-3 py-1.5 rounded-sm transition-colors"
                  data-testid="group-banner">
              <Users className="h-3.5 w-3.5" />
              Part of a household · {s.group_role?.replace("_", " ")}
              <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
            </Link>
          )}
          {s.group_id && user?.role !== "applicant" && (
            <HouseholdMembersStrip screening={s} isStaff={isStaff} />
          )}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div>
              <div className="ntn-mono mb-1">ID · {s.id.slice(0, 8).toUpperCase()}</div>
              <InquiryNumberInline
                screeningId={s.id}
                value={s.inquiry_number || null}
                canEdit={isStaff}
                onSaved={load}
              />
              <h1 className="ntn-h1 mt-3" data-testid="applicant-name">
                {s.applicant_first_name} {s.applicant_last_name}
              </h1>
              <div className="ntn-body mt-2" style={{ color: "#4A5568" }}>{s.applicant_email} · {s.property_address}</div>
              <div className="mt-3 flex flex-wrap gap-2 ntn-caption">
                <span>Submitted by <strong style={{ color: "#0A192F" }}>{s.submitted_by_name}</strong></span>
                {s.submitted_by_company && <span>· {s.submitted_by_company}</span>}
                <span>· {fmtDate(s.created_at)}</span>
              </div>
              {isStaff && (s.ordering_company?.label || s.company || s.submitted_by_company) && (
                <div className="mt-2 inline-flex items-center gap-1.5 bg-[#F3EFEA] border border-[#0A192F]/10 rounded-sm px-2.5 py-1"
                     data-testid="case-ordering-subscriber">
                  <span className="text-[10px] uppercase tracking-[0.14em] text-[#4A5568]">Ordering Subscriber</span>
                  <span className="text-[12px] font-semibold text-[#0A192F]">
                    {s.ordering_company?.label || s.company || s.submitted_by_company}
                    {s.ordering_company?.account_number ? ` · ${s.ordering_company.account_number}` : ""}
                  </span>
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="flex flex-col gap-2 items-end">
                {isStaff && !s.assigned_staff_id && (
                  <Button onClick={claim} size="sm" className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm" data-testid="claim-btn">Claim</Button>
                )}
                {user?.role === "property_manager" && s.application_status !== "completed" && false && (
                  <Dialog open={sendOpen} onOpenChange={(v) => { setSendOpen(v); if (!v) setSendResult(null); }}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm" data-testid="send-application-btn">
                        <Mail className="h-4 w-4 mr-2" /> {s.application_status === "sent" ? "Resend application" : "Send application"}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Send California rental application</DialogTitle>
                        <DialogDescription>
                          We&rsquo;ll email <strong>{s.applicant_email}</strong> a secure link to a 7-step CA-compliant application
                          (FCRA / ICRAA / AB 1418 / FEHA / Megan&rsquo;s Law disclosures + e-signature).
                        </DialogDescription>
                      </DialogHeader>
                      {sendResult ? (
                        <div className="bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-sm p-4">
                          <div className="font-semibold text-[#15803D] text-sm mb-2">Link sent</div>
                          <div className="text-xs text-[#4A5568] font-mono break-all">{sendResult.apply_link}</div>
                          <div className="flex items-center justify-between mt-3">
                            <div className="text-xs text-[#4A5568]">Expires: {fmtDateTime(sendResult.expires_at)}</div>
                            <Button size="sm" variant="outline" className="rounded-sm h-8" data-testid="copy-apply-link"
                              onClick={() => { navigator.clipboard.writeText(sendResult.apply_link); toast.success("Link copied"); }}>
                              Copy link
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Screening fee ($)</Label>
                              <Input type="number" step="0.01" placeholder="50.00" value={sendFee} onChange={(e) => setSendFee(e.target.value)} className="mt-1.5 h-11 rounded-sm" data-testid="send-fee" />
                              <div className="text-[10px] text-[#4A5568] mt-1">2026 cap (CCC §1950.6) ≈ $62.02</div>
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Receipt #</Label>
                              <Input placeholder="NTN-INV-2026-0042" value={sendReceipt} onChange={(e) => setSendReceipt(e.target.value)} className="mt-1.5 h-11 rounded-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Message to applicant (optional)</Label>
                            <Textarea rows={3} value={sendNote} onChange={(e) => setSendNote(e.target.value)} className="mt-1.5 rounded-sm" />
                          </div>
                        </div>
                      )}
                      <DialogFooter>
                        {sendResult ? (
                          <Button onClick={() => setSendOpen(false)} className="bg-[#0A192F] rounded-sm">Done</Button>
                        ) : (
                          <Button onClick={sendApplicationLink} disabled={sendBusy} className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm" data-testid="send-application-confirm">
                            {sendBusy ? "Sending…" : "Send link"}
                          </Button>
                        )}
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
                {s.application_status === "completed" && (
                  <Button onClick={exportApplicationPdf} size="sm" variant="outline" className="rounded-sm" data-testid="export-app-pdf-btn">
                    <FileSignature className="h-4 w-4 mr-2" /> CA Application PDF
                  </Button>
                )}
                {/* Phase D: Export PDF gated by reports.download. NTN staff bypass. */}
                {(user?.role === "ntn_staff" || (hasPermission?.("reports.download") && s.recommendation)) && (
                  <Button onClick={exportPdf} size="sm" variant="outline" className="rounded-sm" data-testid="export-pdf-btn">
                    <Download className="h-4 w-4 mr-2" /> Download Screening Summary
                  </Button>
                )}
                {/* Modify Order — PM (own) OR NTN Staff, only while the
                    application is still open. Blocked once a Final Decision
                    is recorded (backend also gates with 409). */}
                {(user?.role === "property_manager" || user?.role === "ntn_staff") && !s.final_decision && (
                  <ModifyOrderDialog screening={s} onModified={load} />
                )}
                {/* Manual Archive is hidden for Property Managers — closing a
                    final decision (Approved / Not Approved / Not Selected)
                    auto-archives via the DecisionHero "Record Final Decision"
                    action. NTN staff retain the manual archive control as an
                    administrative override. */}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline"
                      className={`rounded-sm ${s.archived_at ? "border-[#22C55E]/40 text-[#15803D] hover:bg-[#22C55E]/10" : "border-[#0A192F]/20"} ${user?.role === "applicant" || user?.role === "property_manager" ? "hidden" : ""}`}
                      data-testid="archive-btn">
                      {s.archived_at
                        ? <><ArchiveRestore className="h-4 w-4 mr-2" /> Restore</>
                        : <><Archive className="h-4 w-4 mr-2" /> Archive</>}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {s.archived_at ? "Restore this screening?" : "Archive this screening?"}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {s.archived_at
                          ? "It will reappear in your active dashboard. All data is preserved."
                          : "It will be hidden from the active dashboard but kept in your archive. You can restore it anytime."}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={toggleArchive} className="bg-[#0A192F]" data-testid="archive-confirm">
                        {s.archived_at ? "Restore" : "Archive"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              {s.application_status && (
                <div className="text-xs text-[#4A5568] mt-2" data-testid="app-status">
                  Rental application: <span className={`font-mono px-1.5 py-0.5 rounded-sm ${s.application_status === "completed" ? "bg-[#22C55E]/15 text-[#15803D]" : "bg-[#F59E0B]/15 text-[#92400E]"}`}>
                    {s.application_status.toUpperCase()}
                  </span>
                </div>
              )}
              {s.assigned_staff_name && <div className="text-xs text-[#4A5568] mt-2">Assigned: <strong className="text-[#0A192F]">{s.assigned_staff_name}</strong></div>}
            </div>
          </div>
        </div>

        {/* 1. EXECUTIVE SUMMARY — Bloomberg-style command-center stripe */}
        <div className="lg:col-span-12">
          <ExecutiveSummaryStripe screening={s} testid="applicant-executive-summary" />
        </div>

        {/* 2. Workflow pipeline stepper */}
        <div className="lg:col-span-12">
          <VerificationPipelineStepper
            currentStageKey={stageFor(s).key}
            title="Verification Workflow"
            subtitle="Where this screening sits in the NTN verification process"
            testid="applicant-stepper"
          />
        </div>


        {/* 3. Additional Information Needed (full-width when active) */}
        {(stageFor(s).key === "additional" || s.additional_info) && (
          <div className="lg:col-span-12">
            <AdditionalInfoPanel screening={s} isStaff={isStaff} onUpdate={load} />
          </div>
        )}

        {/* ──────────── 2-COLUMN INFORMATION ARCHITECTURE ────────────
            Left  (8/12): Decision Intelligence — NTN Recommendation, Risk
                          Intelligence Summary, Risk Indicators.
            Right (4/12): Supporting Intelligence — DecisionPoint Summary,
                          Verification & Validation Findings.
            Both columns scroll independently within the page; bottom band
            (Documents + Activity) is full width. */}

        {/* LEFT COLUMN — Primary Decision Intelligence (50/50 split) */}
        <div className="lg:col-span-6 space-y-6" data-testid="primary-decision-intelligence">
          {/* 4. NTN Recommendation hero (most prominent decision surface) */}
          <DecisionHero s={s} isStaff={isStaff} isPM={user?.role === "property_manager"} reload={load} />

          {/* 5. Risk Indicators — single unified card (summary headline + list) */}
          <RiskIndicatorsSection
            screeningId={s.id}
            isStaff={isStaff}
            applicantRisk={s.applicant_risk_summary}
            onApplicantRiskSaved={(v) => setS({ ...s, applicant_risk_summary: v })}
          />
        </div>

        {/* RIGHT COLUMN — Supporting Intelligence (50/50 split, visually de-emphasized) */}
        <div className="lg:col-span-6 space-y-6" data-testid="supporting-intelligence">
          {/* 7. DecisionPoint Summary — supporting intelligence only */}
          <DecisionPointSummaryCard screening={s} isStaff={isStaff} />
          <SoloHouseholdIncomeCard screening={s} isStaff={isStaff} onUpdated={load} />

          {/* 8. Verification & Validation Findings */}
          <VerificationFindingsCard screening={s} testid="verification-findings" />

          {/* v1.0.8 — per-entry employment/rental-history verification
              structure + deliberately shared NTN findings (on-screen only). */}
          <VerificationEntriesDisplay sid={s.id} />

          {/* 9. Order History — Supporting Intelligence surface that shows the
              lifecycle of the order (original + added services). Renders only
              when order_history[] is present on the record. */}
          <OrderHistoryCard screening={s} />

          {/* 10. Case Communication Log — supporting case context. Replaces the
              legacy bottom-of-page chat thread per platform spec. Lives in the
              right column so PMs can scan prior communication during review. */}
          <CaseCommunicationLog
            messages={messages}
            currentUserId={user.id}
            onSend={sendMessage}
          />
        </div>

        {/* Evidence Layer band — Documents + Activity logs + supporting tabs */}
        <div className="lg:col-span-12 mt-2" data-testid="evidence-layer">
          <div className="flex items-center gap-2 mb-3">
            <span className="ntn-eyebrow">Evidence Layer</span>
            <span className="text-slate-300">·</span>
            <span className="ntn-caption font-medium">Documents · Activity · Detail</span>
          </div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="h-auto bg-[#F3EFEA] p-1 rounded-sm flex-wrap">
              {/* Phase D: Documents tab visible only if user has documents.view */}
              {(user?.role === "ntn_staff" || user?.role === "applicant" || hasPermission?.("documents.view")) && (
                <TabsTrigger value="documents" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-documents">Documents</TabsTrigger>
              )}
              {user?.role !== "applicant" && (
                <TabsTrigger value="operations" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-operations">
                  <ListChecks className="h-3.5 w-3.5 mr-1.5" />
                  Activity{!isStaff && " & Progress"}
                </TabsTrigger>
              )}
              <TabsTrigger value="reports" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-reports">Reports</TabsTrigger>
              <TabsTrigger value="decisionpoint" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-decisionpoint">
                <BarChart3 className="h-3.5 w-3.5 mr-1.5" />DecisionPoint Detail
              </TabsTrigger>
              {(user?.role === "ntn_staff" || wasProductOrdered(s, "employment_verification")) && (
              <TabsTrigger value="employment_verification" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-employment-verification">
                <Briefcase className="h-3.5 w-3.5 mr-1.5" />Employment Verification
              </TabsTrigger>
              )}
              {(user?.role === "ntn_staff" || wasProductOrdered(s, "landlord_verification")) && (
              <TabsTrigger value="landlord_verification" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-landlord-verification">
                <Home className="h-3.5 w-3.5 mr-1.5" />Landlord Verification
              </TabsTrigger>
              )}
              {(user?.role === "ntn_staff" || wasProductOrdered(s, "document_validation")) && (
              <TabsTrigger value="document_validation" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-document-validation">
                <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />Document Validation
              </TabsTrigger>
              )}
              <TabsTrigger value="details" className="px-4 py-2 rounded-sm data-[state=active]:bg-white text-sm" data-testid="tab-details">Details</TabsTrigger>
            </TabsList>

            <TabsContent value="reports" className="mt-4">
              <div className="bg-white border border-[#0A192F]/10 rounded-sm">
                {/* Phase E.3 · only render products that were actually
                    ordered. NTN staff still see every row so they can
                    triage historical / mid-migration cases. */}
                {s.reports
                  .filter((r) => user?.role === "ntn_staff" || wasProductOrdered(s, r.type))
                  .map((r) => (
                    <ReportRow key={r.type} report={r} isStaff={isStaff} onUpdate={updateReport} />
                  ))}
              </div>
            </TabsContent>

            <TabsContent value="decisionpoint" className="mt-4 space-y-6">
              {/* For PMs and applicants, show only the visual report. */}
              {!isStaff && (
                s.decisionpoint
                  ? <DecisionPointReport dp={s.decisionpoint} monthlyRent={s.monthly_rent} ordered={s.effective_ordered_products || s.ordered_products || s.services || []} />
                  : (
                    <div className="bg-white border border-dashed border-[#0A192F]/15 rounded-sm p-10 text-center" data-testid="dp-empty-pm">
                      <BarChart3 className="h-8 w-8 text-[#4A5568] mx-auto mb-3" />
                      <div className="font-serif text-xl text-[#0A192F]">DecisionPoint report pending</div>
                      <div className="text-sm text-[#4A5568] mt-2 max-w-md mx-auto">
                        NTN analysts are compiling the credit, eviction, criminal and verification metrics. You&rsquo;ll receive an email when it&rsquo;s ready.
                      </div>
                    </div>
                  )
              )}
              {/* Staff sees the live report (if any) and the entry form below. */}
              {isStaff && (
                <>
                  {s.decisionpoint && <DecisionPointReport dp={s.decisionpoint} monthlyRent={s.monthly_rent} ordered={s.effective_ordered_products || s.ordered_products || s.services || []} />}
                  <DecisionPointEntryForm
                    screeningId={s.id}
                    dp={s.decisionpoint}
                    onSaved={(updated) => setS(updated)}
                  />
                </>
              )}
            </TabsContent>

            {/* Verified module reports — each verification module lives in its
                own tab so PMs can drill into the exact report they need. Each
                pane only renders content when the corresponding verifier has
                explicitly published a summary. */}
            <TabsContent value="employment_verification" className="mt-4 space-y-6">
              {s.verifications?.employment?.pm_summary?.published_at
                ? <PMEmploymentSummaryCard screening={s} testid="pm-employment-summary" />
                : <VerificationReportPending Icon={Briefcase} title="Employment Verification report pending"
                                             description="NTN staff will publish the Verified Employment Report as soon as verification is complete." />}
            </TabsContent>

            <TabsContent value="landlord_verification" className="mt-4 space-y-6">
              {s.verifications?.landlord?.pm_summary?.published_at
                ? <PMLandlordSummaryCard screening={s} testid="pm-landlord-summary" />
                : <VerificationReportPending Icon={Home} title="Landlord Verification report pending"
                                             description="NTN staff will publish the Verified Landlord Report as soon as verification is complete." />}
            </TabsContent>

            <TabsContent value="document_validation" className="mt-4 space-y-6">
              {s.verifications?.documents?.pm_summary?.published_at
                ? <PMDocumentValidationSummaryCard screening={s} testid="pm-documents-summary" />
                : <VerificationReportPending Icon={ShieldCheck} title="Document Validation report pending"
                                             description="NTN staff will publish the Verified Document Validation Report as soon as validation is complete." />}
            </TabsContent>

            <TabsContent value="operations" className="mt-4">
              <WorkflowPanel sid={s.id} />
            </TabsContent>

            <TabsContent value="documents" className="mt-4">
              <div className="bg-white border border-[#0A192F]/10 rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif text-xl text-[#0A192F]">Documents</h3>
                  <input ref={fileInputRef} type="file" multiple className="hidden" onChange={uploadFile} data-testid="upload-file-input" />
                  <Button size="sm" onClick={() => fileInputRef.current?.click()} className="bg-[#0A192F] hover:bg-[#0A192F]/90 text-white rounded-sm" data-testid="upload-doc-btn" disabled={uploading}>
                    <Upload className="h-4 w-4 mr-2" /> {uploading ? "Uploading…" : "Upload"}
                  </Button>
                </div>

                <div
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!dragOver) setDragOver(true); }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
                  onDrop={onDropFiles}
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !uploading) fileInputRef.current?.click(); }}
                  data-testid="documents-dropzone"
                  className={`mb-4 cursor-pointer rounded-sm border-2 border-dashed transition-colors px-6 py-8 text-center
                    ${dragOver
                      ? "border-[#0A192F] bg-[#0A192F]/5"
                      : "border-[#0A192F]/20 bg-[#F3EFEA]/40 hover:bg-[#F3EFEA]/70"}
                    ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                >
                  <Upload className={`h-6 w-6 mx-auto mb-2 ${dragOver ? "text-[#0A192F]" : "text-[#4A5568]"}`} />
                  <div className="text-sm text-[#0A192F]">
                    {uploading
                      ? "Uploading…"
                      : dragOver
                        ? "Drop files to upload"
                        : "Drag & drop files here, or click to browse"}
                  </div>
                  <div className="text-[11px] text-[#4A5568] mt-1">PDF, images, or scans · 25MB max per file · multiple files allowed</div>
                </div>

                {s.documents?.length > 0 ? (
                  <ul className="divide-y divide-[#0A192F]/10" data-testid="documents-list">
                    {s.documents.map((d) => {
                      const isPackageFile = d.label === "rental_application" || d.label === "supporting_document";
                      const canDelete = user?.role === "ntn_staff" || !isPackageFile;
                      return (
                      <li key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid={`doc-${d.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <Paperclip className="h-4 w-4 text-[#4A5568] shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm text-[#0A192F] truncate">{d.filename}</div>
                            <div className="text-xs text-[#4A5568] font-mono">{d.label} · {d.uploaded_by_role} · {fmtDate(d.created_at)}{isPackageFile && user?.role === "property_manager" ? " · locked" : ""}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <a href={`${apiClient.defaults.baseURL}/files/${d.id}/download`}
                             target="_blank" rel="noreferrer"
                             className="text-sm text-[#0A192F] underline px-2"
                             data-testid={`doc-download-${d.id}`}>
                            Download
                          </a>
                          {canDelete && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button type="button"
                                className="h-8 w-8 rounded-sm flex items-center justify-center text-[#4A5568] hover:bg-[#EF4444]/10 hover:text-[#EF4444] transition-colors"
                                title="Delete document"
                                data-testid={`doc-delete-${d.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete this document?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  <strong className="text-[#0A192F]">{d.filename}</strong> will be removed from this screening.
                                  Audit logs and the underlying file are kept by NTN per our retention policy, but no one will see it on the screening anymore.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteFile(d.id)}
                                  className="bg-[#EF4444] hover:bg-[#EF4444]/90"
                                  data-testid={`doc-delete-confirm-${d.id}`}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          )}
                        </div>
                      </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="text-sm text-[#4A5568] py-6 text-center" data-testid="documents-empty">No documents uploaded yet.</div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="details" className="mt-4">
              <div className="bg-white border border-[#0A192F]/10 rounded-sm p-6 space-y-4 text-sm">
                <Detail label="Phone" value={s.applicant_phone || "—"} />
                <Detail label="Property" value={s.property_address} />
                <Detail label="Monthly rent" value={s.monthly_rent ? `$${s.monthly_rent.toLocaleString()}` : "—"} />
                <Detail label="Notes" value={s.notes || "—"} />
                <Detail label="Services requested" value={s.services.map(reportLabel).join(", ")} />
              </div>
              {isStaff && <DangerZone screening={s} />}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AppShell>
  );
}

function Detail({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-[#0A192F]/10 last:border-0">
      <div className="font-overline text-[#4A5568]">{label}</div>
      <div className="col-span-2 text-[#0A192F]">{value}</div>
    </div>
  );
}


function VerificationReportPending({ Icon, title, description }) {
  return (
    <div className="bg-white border border-dashed border-[#0A192F]/15 rounded-sm p-10 text-center">
      {Icon ? <Icon className="h-8 w-8 text-[#4A5568] mx-auto mb-3" /> : null}
      <div className="font-serif text-xl text-[#0A192F]">{title}</div>
      <div className="text-sm text-[#4A5568] mt-2 max-w-md mx-auto">{description}</div>
    </div>
  );
}
